/**
 * When the CSV modal opens, sync column keys from default template or full spec.
 * Re-syncs if the column spec signature changes while the modal stays open.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';

import type { CsvColumnSpec } from '../buildCsvColumns';
import { normalizeExportColumnOrder } from '../csvExportColumnOrder';
import {
  pickDefaultTemplate,
  type CsvExportTemplate,
} from '../useCsvExportTemplates';

const DEFAULT_TEMPLATE_COLOR = '#3b82f6';
const COL_SIG = '\u0001';

function applyTemplateDownloadName(
  filenameTouched: boolean,
  setName: Dispatch<SetStateAction<string>>,
  name: string,
): void {
  if (!filenameTouched) setName(name);
}

export function useCsvExportModalOpenSync(
  open: boolean,
  tab: string,
  columnKeySig: string,
  columns: CsvColumnSpec[],
  remoteTemplates: CsvExportTemplate[],
  templatesReady: boolean,
  setExportColumnKeys: Dispatch<SetStateAction<string[]>>,
  setExportDownloadBaseName: Dispatch<SetStateAction<string>>,
  exportDownloadFilenameTouched: boolean,
  setTemplateName: Dispatch<SetStateAction<string>>,
  setTemplateColor: Dispatch<SetStateAction<string>>,
  setSaveAsDefault: Dispatch<SetStateAction<boolean>>,
  setEditingTemplateId: Dispatch<SetStateAction<string | null>>,
): void {
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const initialColumnSyncForOpenDoneRef = useRef(false);
  const lastSyncedSigRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      initialColumnSyncForOpenDoneRef.current = false;
      lastSyncedSigRef.current = null;
      setEditingTemplateId(null);
      setTemplateName('');
      setTemplateColor(DEFAULT_TEMPLATE_COLOR);
      setSaveAsDefault(false);
      return;
    }

    if (!templatesReady) return;

    const needInitialSync = !initialColumnSyncForOpenDoneRef.current;
    const specChanged =
      lastSyncedSigRef.current !== null &&
      lastSyncedSigRef.current !== columnKeySig;
    const shouldSync = needInitialSync || specChanged;

    if (!shouldSync) return;

    if (needInitialSync) initialColumnSyncForOpenDoneRef.current = true;
    lastSyncedSigRef.current = columnKeySig;
    const keys = columnsRef.current.map((c) => c.key);
    const def = pickDefaultTemplate(remoteTemplates, tab);
    const fromTpl = Array.isArray(def?.columns) ? def.columns : [];
    if (fromTpl.length > 0 && def) {
      const tplCols = fromTpl.filter((k): k is string => typeof k === 'string');
      const normalized = normalizeExportColumnOrder(keys, tplCols);
      setExportColumnKeys(normalized);
      applyTemplateDownloadName(
        exportDownloadFilenameTouched,
        setExportDownloadBaseName,
        typeof def.downloadBaseName === 'string' ? def.downloadBaseName : '',
      );
      const storedSig = tplCols.join(COL_SIG);
      const appliedSig = normalized.join(COL_SIG);
      if (storedSig !== appliedSig) {
        setEditingTemplateId(null);
        setTemplateName('');
        setTemplateColor(DEFAULT_TEMPLATE_COLOR);
        setSaveAsDefault(false);
      } else {
        setTemplateName(def.name);
        setTemplateColor(def.color);
        setSaveAsDefault(def.isDefault);
        setEditingTemplateId(def.id);
      }
      return;
    }

    setExportColumnKeys([...keys]);
    applyTemplateDownloadName(
      exportDownloadFilenameTouched,
      setExportDownloadBaseName,
      typeof def?.downloadBaseName === 'string' ? def.downloadBaseName : '',
    );
    setTemplateName('');
    setTemplateColor(DEFAULT_TEMPLATE_COLOR);
    setSaveAsDefault(false);
    setEditingTemplateId(null);
  }, [
    open,
    tab,
    columnKeySig,
    remoteTemplates,
    templatesReady,
    setExportColumnKeys,
    setExportDownloadBaseName,
    setTemplateName,
    setTemplateColor,
    setSaveAsDefault,
    setEditingTemplateId,
    exportDownloadFilenameTouched,
  ]);
}
